// @flow
import React, { Component } from 'react';
import BoardPickerRow from 'app/common/react/components/save/pin-to-board/BoardPickerPane/BoardPickerRow';
import BoardSuggestionList from 'app/common/react/components/save/pin-to-board/BoardSuggestionList/BoardSuggestionList';
import CreateBoardButton from 'app/common/react/components/save/pin-to-board/CreateBoardButton/CreateBoardButton';
import i18n from 'app/common/lib/i18n';
import IndexManager from 'app/common/react/components/common/SectionList/IndexManager';
import PickerSearchField from 'app/common/react/components/RepinPicker/PickerSearchField/PickerSearchField';
import type { PinToBoardSavedToType } from 'app/common/react/components/save/pin-to-board/PinToBoard/PinToBoard';
import type { RepinBoardSection } from 'app/common/redux/reducers/sections';
import type { State as Board } from 'app/common/redux/reducers/board';
import { stopTiming } from 'app/common/lib/TraceLogger/logTraceEvent';
import SectionList, {
  type ItemRenderProps,
  type Section,
} from 'app/common/react/components/common/SectionList/SectionList';
import { Box, Divider, Spinner, Text } from 'gestalt';

type Props = {|
  boardSavedTo: ?PinToBoardSavedToType,
  createButtonLabel?: string,
  disabledRowId?: string,
  getSections: (searchQuery: string) => Array<Section>,
  hasLoadError?: boolean,
  hasLowBoardCount?: boolean,
  isLoaded: boolean,
  isSelectMode?: boolean,
  onCreate?: (name: string) => void,
  onSelected: (item: Board | RepinBoardSection) => void,
  pinId?: string,
|};

const SEARCH_FIELD_EMPTY = '';
// prevent single character searches, which are (1) not very useful since single characters like "a" would match
// a majority of the user's boards, and (2) short query re-renders are visually distracting
export const SEARCH_FIELD_QUERY_LENGTH_MIN = 2;

type State = {
  searchQuery: string,
};

export class RepinPicker extends Component<Props, State> {
  state = { searchQuery: SEARCH_FIELD_EMPTY };

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.isLoaded && this.props.isLoaded) {
      this.setPWTLoadingEnd();
    }
  }

  setPWTLoadingEnd() {
    stopTiming(PCONST.thrift.TracingClientTypes.PWTAction.BOARD_PICKER, {
      error: this.props.hasLoadError,
    });
  }

  handleSelectItem = (item?: Board | RepinBoardSection) => {
    const { onCreate, onSelected } = this.props;
    if (!item && onCreate) {
      // This means the user typed a new search query that doesn't exist yet
      const { searchQuery } = this.state;
      onCreate(searchQuery);
    } else if (item) {
      onSelected(item);
    }
    // only clear the data if the user selected something or if there is an onCreate
    if (onCreate || item) {
      this.setState({
        searchQuery: '',
      });
    }
  };

  handleSearchQueryInput = (query: string) => {
    this.setState({
      searchQuery: query,
    });
  };

  indexManager: IndexManager;

  renderSectionHeader = (section: Section, index: number) =>
    section.title ? (
      <Box>
        {index > 0 && <Divider />}
        <Box deprecatedPadding={{ x: 3, y: 1 }}>
          <Text size="sm">{section.title}</Text>
        </Box>
      </Box>
    ) : null;

  renderRow = ({ item, hovered, sectionProps = {} }: ItemRenderProps) => {
    const { boardSavedTo, isSelectMode, disabledRowId } = this.props;
    const { searchQuery } = this.state;
    const { inSectionSelection = false } = sectionProps;
    const isSavingToCurrentBoard = boardSavedTo && boardSavedTo.board === item.id;
    const saveState = isSavingToCurrentBoard ? boardSavedTo && boardSavedTo.state : null;
    const isSection = item.type === 'board_section';
    return (
      <BoardPickerRow
        isSelectMode={isSelectMode}
        disabled={item.id === disabledRowId && inSectionSelection}
        item={item}
        isSection={isSection}
        isWithinSectionPicker={inSectionSelection}
        saveState={saveState}
        searchQuery={searchQuery}
        selected={hovered}
        size="md"
      />
    );
  };

  render() {
    const {
      boardSavedTo,
      createButtonLabel,
      hasLowBoardCount,
      getSections,
      isLoaded,
      onCreate,
      pinId,
    } = this.props;

    if (!isLoaded) {
      return (
        <Box deprecatedPadding={{ y: 5 }}>
          <Spinner
            accessibilityLabel={i18n._(
              'loading section picker',
              'accessibility label for the loading spinner the repin picker',
            )}
            show
          />
        </Box>
      );
    } else {
      const { searchQuery } = this.state;
      const sections = getSections(searchQuery);
      this.indexManager = new IndexManager(sections.map((section) => section.items));
      this.indexManager.setSelectItem(this.handleSelectItem);

      return (
        <Box
          minHeight={0}
          flex="grow"
          height="100%"
          overflow="auto"
          display="flex"
          direction="column"
          dangerouslySetInlineStyle={{
            __style: { zIndex: 0 }, // HACK to fix search field dropshadow in Chrome. See BUG-69956.
          }}
        >
          {!hasLowBoardCount && (
            <Box>
              <Box paddingX={6} marginBottom={4} marginTop={1}>
                <PickerSearchField
                  disabled={!!boardSavedTo}
                  indexManager={this.indexManager}
                  onChange={this.handleSearchQueryInput}
                  value={searchQuery}
                />
              </Box>
              <Divider />
            </Box>
          )}
          <SectionList
            disabled={!!boardSavedTo}
            grow={!hasLowBoardCount && !searchQuery}
            indexManager={this.indexManager}
            onSelectItem={this.handleSelectItem}
            renderItem={this.renderRow}
            renderSectionHeader={this.renderSectionHeader}
            sections={sections}
          />
          <Divider />
          {onCreate && (
            // $FlowFixMe: new errors found from flow 0.96 upgrade
            <CreateBoardButton
              buttonLabel={createButtonLabel}
              hasHoverState
              name={searchQuery}
              onClick={() => onCreate(searchQuery)}
            />
          )}
          {hasLowBoardCount &&
            pinId && <BoardSuggestionList onBoardSelected={onCreate} pinId={pinId} />}
        </Box>
      );
    }
  }
}

export default RepinPicker;
